import React, {useContext, useEffect, useState, useRef, useCallback} from 'react'
import {IRecipe} from '../../../api/IRecipe'
import { getRecipes} from '../../../api/api'
import UserContext from '../../../hooks/UserContext'
import {Container, Box, Grid, IconButton, Typography} from "@mui/material";
import RecipeList from "./RecipeList";
import ActionBar from "../../../components/ActionBar";
import {PageHeader} from "../../../components/PageHeader";
import {useNavigate} from "react-router-dom";
import {CREATE_RECIPE} from "../../../routes";
import TableRowsIcon from '@mui/icons-material/TableRows';
import GridViewIcon from '@mui/icons-material/GridView';
import {useLocalStorage} from "../../../hooks/useLocalStorage";
import { useTranslation } from 'react-i18next';
import RecipeSearchBar from "../../../components/RecipeSearchBar/RecipeSearchBar";

interface IRecipeListPageProps {
  view?: string
  base?: boolean
}
const RecipeListPage = ({view, base}:IRecipeListPageProps): JSX.Element => {
  const { t } = useTranslation();
  const RECIPE_VIEW = "recipe_view"
  const [viewType, setViewType] = useLocalStorage<string>(
    RECIPE_VIEW,
    "RecipeCard",
  )
  const {user} = useContext(UserContext)
  const [recipes, setRecipes] = useState<{list:IRecipe[], next: string}>({list: [], next: ""})
  const [searchResults, setSearchResults] = useState<IRecipe[] | null>(null)
  const [error, setError] = useState(null)
  const [page, setPage] = useState(1)
  const [refresh, setRefresh] = useState(false)
  const loader = useRef(null);
  const fetching = useRef(false);
  const navigate = useNavigate()

  const isSearchActive = searchResults !== null;
  const displayRecipes = isSearchActive ? searchResults : recipes.list;

  const handleSearchResults = useCallback((results: IRecipe[] | null) => {
    setSearchResults(results);
  }, []);

  const fetch = async() => {
    try {
      fetching.current = true
      if(recipes.next == null) {
        return
      }
      let limit = 26
      const mediaQueryMedium = window.matchMedia("(max-width: 768px)")
      if (mediaQueryMedium.matches) {
        limit = 12
      }
      const mediaQueryXS = window.matchMedia("(max-width: 576px)")
      if (mediaQueryXS.matches) {
        limit = 12
      }
      const nextRecipes = await getRecipes(recipes.next, limit, false)
      const newList = recipes.list.concat(nextRecipes.recipes)
      setRecipes(() => {
        return {list: newList, next: nextRecipes.next}
      })
    } catch (err) {
      setError(err)
    } finally {
      fetching.current = false
    }
  }

  const handleObserver = (entities) => {
    const target = entities[0];
    if (target.isIntersecting) {
      setPage((p) => p + 1)
    }
  }

  useEffect(() => {
    const options = {
      root: null,
      rootMargin: "20px",
      threshold: 1.0
    };
    const observer = new IntersectionObserver(handleObserver, options);
    if (loader.current) {
      observer.observe(loader.current)
    }
  }, []);

  useEffect(() => {
      setRecipes(() => {
        return {list: [], next: ""}
      })
      setPage(() => {return 1})
      setRefresh(false)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [refresh])

  useEffect(() => {
      if (fetching.current) {
        return
      }
      fetch()
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [page])

  if (!recipes && !error) {
    return <></>
  }

  return (
    <Container>
      <ActionBar
        error={error}
        action={
          (user?.roles?.includes('creator')) ? (
            <div className={"ms-auto text-end"}>
              <PageHeader.Actions >
                <IconButton
                  onClick={() => {
                    if (viewType === "RecipeCard") {
                      setViewType("RecipeTable")
                    } else {
                      setViewType("RecipeCard")
                    }
                  }}>
                  {viewType === "RecipeCard" && (<GridViewIcon />)}
                  {viewType === "RecipeTable" && (<TableRowsIcon />)}
                </IconButton>
                <PageHeader.ActionItem
                  variant="contained"
                  onClick={() => navigate(`${CREATE_RECIPE}`)}>
                  {t('recipe.addRecipe')}
                </PageHeader.ActionItem>
              </PageHeader.Actions>
            </div>) : (
            <div className={"ms-auto text-end"}>
            </div>
          )
        }
      />
      <RecipeSearchBar onSearchResults={handleSearchResults} />
      <Box marginTop={1}>
        {!error && (
          <>
            {isSearchActive && displayRecipes.length === 0 ? (
              <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
                {t('recipe.noRecipes')}
              </Typography>
            ) : (
              <Grid container spacing={{xs: 1}}>
                <RecipeList
                  recipes={displayRecipes}
                  view={viewType}
                  setError={setError}
                  setRefresh={setRefresh}
                />
              </Grid>
            )}
            {!isSearchActive && (
              <div className="loading" ref={loader}>
                {recipes.next != null  && (<h3>{t('common.moreItems', { items: t('navigation.recipes').toLowerCase() })}</h3>)}
              </div>
            )}
          </>
        )}
      </Box>
    </Container>
  )
}
export default RecipeListPage
